import React, { useState } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { changePassword } from '../../api/auth';

interface NavItemProps {
  to: string;
  label: string;
  icon: React.ReactNode;
  end?: boolean;
  onNavigate?: () => void;
}

function NavItem({ to, label, icon, end, onNavigate }: NavItemProps) {
  const location = useLocation();
  const isActive = end
    ? location.pathname === to
    : location.pathname === to || location.pathname.startsWith(to + '/');
  return (
    <NavLink
      to={to}
      className={`nav-item ${isActive ? 'active' : ''}`}
      end={end}
      onClick={onNavigate}
    >
      <span className="icon">{icon}</span>
      {label}
    </NavLink>
  );
}

function IconDashboard() {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5">
      <rect x="1" y="1" width="6" height="6" rx="1" /><rect x="9" y="1" width="6" height="6" rx="1" />
      <rect x="1" y="9" width="6" height="6" rx="1" /><rect x="9" y="9" width="6" height="6" rx="1" />
    </svg>
  );
}

function IconBuilding() {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5">
      <rect x="2" y="3" width="12" height="11" rx="1" />
      <path d="M5 14V9h6v5" /><path d="M5 6h1M8 6h1M11 6h1M5 3V1M11 3V1" strokeLinecap="round" />
    </svg>
  );
}

function IconContacts() {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5">
      <circle cx="8" cy="5" r="3" /><path d="M2 14a6 6 0 0112 0" strokeLinecap="round" />
    </svg>
  );
}

function IconMandate() {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5">
      <rect x="2" y="1" width="12" height="14" rx="1" />
      <path d="M5 5h6M5 8h6M5 11h4" strokeLinecap="round" />
    </svg>
  );
}

function IconCompany() {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5">
      <rect x="1" y="4" width="14" height="11" rx="1" />
      <path d="M4 4V2a1 1 0 011-1h6a1 1 0 011 1v2" />
      <path d="M1 8h14" strokeLinecap="round" />
      <rect x="6" y="6" width="4" height="4" rx="0.5" />
    </svg>
  );
}

function IconEmail() {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5">
      <rect x="1" y="3" width="14" height="10" rx="1" />
      <path d="M1 4l7 5 7-5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function IconInbox() {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5">
      <path d="M1 10v3a1 1 0 001 1h12a1 1 0 001-1v-3" strokeLinecap="round" />
      <path d="M1 10l2-7a1 1 0 011-1h8a1 1 0 011 1l2 7" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M1 10h4l1 2h4l1-2h4" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function IconSettings() {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5">
      <circle cx="8" cy="8" r="2.5" />
      <path d="M8 1v2M8 13v2M1 8h2M13 8h2M3.05 3.05l1.41 1.41M11.54 11.54l1.41 1.41M3.05 12.95l1.41-1.41M11.54 4.46l1.41-1.41" strokeLinecap="round" />
    </svg>
  );
}

function IconUsers() {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5">
      <circle cx="6" cy="5" r="2.5" />
      <path d="M1 14a5 5 0 0110 0" strokeLinecap="round" />
      <circle cx="12" cy="5" r="2" />
      <path d="M11 14a4 4 0 013.5-4" strokeLinecap="round" />
    </svg>
  );
}

function IconLogout() {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5">
      <path d="M6 2H3a1 1 0 00-1 1v10a1 1 0 001 1h3" strokeLinecap="round" />
      <path d="M11 11l3-3-3-3M14 8H6" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

interface SidebarProps {
  isOpen?: boolean;
  onClose?: () => void;
}

export function Sidebar({ isOpen, onClose }: SidebarProps) {
  const { user, logout, isAdmin } = useAuth();
  const [showProfile, setShowProfile] = useState(false);

  return (
    <nav className={`sidebar${isOpen ? ' sidebar-open' : ''}`}>
      <button className="sidebar-close" onClick={onClose} aria-label="Menü schließen">×</button>

      <div className="logo" style={{ padding: '20px 16px 12px', textAlign: 'center' }}>
        <img src="/assets/logo.svg" alt="Rubin Real Estate" style={{ width: '100%', maxWidth: 160, display: 'block', margin: '0 auto' }} />
        <div style={{ fontSize: 10, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: '0.08em', marginTop: 6 }}>
          Investment CRM <span style={{ fontSize: 9, opacity: 0.6 }}>v1.11.2</span>
        </div>
      </div>

      <div className="nav">
        <NavItem to="/dashboard" label="Dashboard" icon={<IconDashboard />} end onNavigate={onClose} />
        <div className="nav-section">CRM</div>
        <NavItem to="/companies" label="Investoren & Profile" icon={<IconBuilding />} onNavigate={onClose} />
        <NavItem to="/duplikate" label="Doppelte Firmen" icon={<IconBuilding />} onNavigate={onClose} />
        <NavItem to="/contacts" label="Kontakte" icon={<IconContacts />} onNavigate={onClose} />
        <NavItem to="/verkaufsmandate" label="Verkaufsmandate" icon={<IconMandate />} onNavigate={onClose} />
        <NavItem to="/kommunikation" label="Kommunikation" icon={<IconInbox />} onNavigate={onClose} />
        <NavItem to="/review" label="Review-Queue (Profile)" icon={<IconBuilding />} onNavigate={onClose} />
        <NavItem to="/jobs" label="Hintergrund-Jobs" icon={<IconSettings />} onNavigate={onClose} />
        <div className="nav-section">Verwaltung</div>
        <NavItem to="/unternehmen" label="Unternehmen" icon={<IconCompany />} onNavigate={onClose} />
        <NavItem to="/mail-einstellungen" label="Mail-Einstellungen" icon={<IconEmail />} onNavigate={onClose} />
        <NavItem to="/einstellungen" label="Einstellungen" icon={<IconSettings />} onNavigate={onClose} />
        {isAdmin && (
          <NavItem to="/benutzer" label="Benutzer" icon={<IconUsers />} onNavigate={onClose} />
        )}
      </div>

      {/* Hilfe-Shortcut direkt über User-Block */}
      <div style={{ flexShrink: 0, padding: '8px 12px', borderTop: '1px solid var(--border)' }}>
        <a href="/docs/Benutzerhandbuch.html" target="_blank" rel="noopener noreferrer"
          title="Benutzerhandbuch"
          style={{
            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px',
            padding: '7px 10px', background: 'var(--surface2)', color: 'var(--text2)',
            textDecoration: 'none', borderRadius: 7, fontSize: 12, fontWeight: 500,
          }}>
          📖 Handbuch
        </a>
      </div>

      {/* User-Info + Logout am unteren Rand */}
      <div style={{ flexShrink: 0, padding: '16px 12px', borderTop: '1px solid var(--border)' }}>
        {/* Klick auf User-Bereich → Profil-Modal */}
        <button
          onClick={() => setShowProfile(true)}
          style={{
            width: '100%', display: 'flex', alignItems: 'center', gap: 10,
            marginBottom: 8, padding: '6px 8px', background: 'none',
            border: 'none', borderRadius: 7, cursor: 'pointer', textAlign: 'left',
            transition: 'background 0.15s',
          }}
          onMouseEnter={e => (e.currentTarget.style.background = 'var(--surface2)')}
          onMouseLeave={e => (e.currentTarget.style.background = 'none')}
        >
          <div style={{
            width: 30, height: 30, borderRadius: '50%',
            background: user?.role === 'admin' ? 'var(--accent)' : '#6366f1',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: 13, fontWeight: 700, color: '#fff', flexShrink: 0,
          }}>
            {user?.full_name?.charAt(0).toUpperCase() ?? '?'}
          </div>
          <div style={{ overflow: 'hidden', flex: 1 }}>
            <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
              {user?.full_name}
            </div>
            <div style={{ fontSize: 11, color: 'var(--text3)' }}>
              {user?.email}
            </div>
          </div>
          <svg width="13" height="13" viewBox="0 0 16 16" fill="none" stroke="var(--text3)" strokeWidth="1.5" style={{ flexShrink: 0 }}>
            <path d="M11 2l-6 6 6 6" strokeLinecap="round" strokeLinejoin="round" transform="rotate(180 8 8)" />
          </svg>
        </button>

        <button
          onClick={() => logout()}
          style={{
            width: '100%', display: 'flex', alignItems: 'center', gap: 8,
            padding: '7px 10px', background: 'var(--surface2)',
            border: '1px solid var(--border)', borderRadius: 7,
            color: 'var(--text2)', fontSize: 13, cursor: 'pointer',
            transition: 'background 0.15s', boxSizing: 'border-box',
          }}
          onMouseEnter={e => (e.currentTarget.style.background = 'var(--border)')}
          onMouseLeave={e => (e.currentTarget.style.background = 'var(--surface2)')}
        >
          <IconLogout /> Abmelden
        </button>
      </div>

      {showProfile && <ProfileModal onClose={() => setShowProfile(false)} />}
    </nav>
  );
}

// ── Profil-Modal ───────────────────────────────────────────────────────
function ProfileModal({ onClose }: { onClose: () => void }) {
  const { user, updateProfile } = useAuth();
  const [tab, setTab] = useState<'profile' | 'password'>('profile');

  // Profil-Tab
  const [fullName, setFullName] = useState(user?.full_name ?? '');
  const [email,    setEmail]    = useState(user?.email ?? '');
  const [saving,   setSaving]   = useState(false);
  const [msg,      setMsg]      = useState('');
  const [err,      setErr]      = useState('');

  // Passwort-Tab
  const [curPw,   setCurPw]  = useState('');
  const [newPw,   setNewPw]  = useState('');
  const [newPw2,  setNewPw2] = useState('');
  const [pwMsg,   setPwMsg]  = useState('');
  const [pwErr,   setPwErr]  = useState('');
  const [pwSaving,setPwSaving] = useState(false);

  async function handleProfileSave(e: React.FormEvent) {
    e.preventDefault();
    setErr(''); setMsg('');
    setSaving(true);
    try {
      await updateProfile({ full_name: fullName, email });
      setMsg('Profil gespeichert.');
    } catch (e: unknown) {
      setErr((e as { response?: { data?: { error?: string } } })?.response?.data?.error ?? 'Fehler beim Speichern.');
    } finally { setSaving(false); }
  }

  async function handlePasswordSave(e: React.FormEvent) {
    e.preventDefault();
    setPwErr(''); setPwMsg('');
    if (newPw !== newPw2) { setPwErr('Passwörter stimmen nicht überein.'); return; }
    if (newPw.length < 8) { setPwErr('Mindestens 8 Zeichen.'); return; }
    setPwSaving(true);
    try {
      await changePassword(curPw, newPw);
      setPwMsg('Passwort geändert.');
      setCurPw(''); setNewPw(''); setNewPw2('');
    } catch (e: unknown) {
      setPwErr((e as { response?: { data?: { error?: string } } })?.response?.data?.error ?? 'Fehler.');
    } finally { setPwSaving(false); }
  }

  const tabBtn = (t: 'profile' | 'password', label: string) => (
    <button onClick={() => setTab(t)} style={{
      padding: '7px 16px', borderRadius: 7, border: 'none', cursor: 'pointer', fontSize: 13, fontWeight: 500,
      background: tab === t ? 'var(--accent)' : 'var(--surface2)',
      color: tab === t ? '#fff' : 'var(--text2)',
    }}>{label}</button>
  );

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 2000 }}>
      <div style={{ background: '#fff', borderRadius: 12, padding: 28, width: 420, maxWidth: '90vw', boxShadow: '0 8px 32px rgba(0,0,0,0.15)' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
          <h3 style={{ margin: 0, fontSize: 17, fontWeight: 700 }}>Mein Profil</h3>
          <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: 22, cursor: 'pointer', color: '#666', lineHeight: 1 }}>×</button>
        </div>

        <div style={{ display: 'flex', gap: 6, marginBottom: 20 }}>
          {tabBtn('profile', 'Profildaten')}
          {tabBtn('password', 'Passwort')}
        </div>

        {tab === 'profile' && (
          <form onSubmit={handleProfileSave}>
            {err && <div style={{ padding: '8px 12px', background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 6, color: '#dc2626', fontSize: 13, marginBottom: 12 }}>{err}</div>}
            {msg && <div style={{ padding: '8px 12px', background: '#f0fdf4', border: '1px solid #86efac', borderRadius: 6, color: '#16a34a', fontSize: 13, marginBottom: 12 }}>{msg}</div>}
            <div style={{ marginBottom: 14 }}>
              <label style={{ display: 'block', fontSize: 13, fontWeight: 500, color: '#555', marginBottom: 5 }}>Name</label>
              <input value={fullName} onChange={e => setFullName(e.target.value)} required style={inp} />
            </div>
            <div style={{ marginBottom: 14 }}>
              <label style={{ display: 'block', fontSize: 13, fontWeight: 500, color: '#555', marginBottom: 5 }}>E-Mail</label>
              <input type="email" value={email} onChange={e => setEmail(e.target.value)} required style={inp} />
            </div>
            <div style={{ marginBottom: 14 }}>
              <label style={{ display: 'block', fontSize: 13, fontWeight: 500, color: '#555', marginBottom: 5 }}>Rolle</label>
              <input value={user?.role === 'admin' ? 'Admin' : 'Benutzer'} disabled style={{ ...inp, background: '#f5f5f5', color: '#888' }} />
            </div>
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 20 }}>
              <button type="button" onClick={onClose} style={cancelBtn}>Schließen</button>
              <button type="submit" disabled={saving} style={saveBtn}>{saving ? 'Speichern...' : 'Speichern'}</button>
            </div>
          </form>
        )}

        {tab === 'password' && (
          <form onSubmit={handlePasswordSave}>
            {pwErr && <div style={{ padding: '8px 12px', background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 6, color: '#dc2626', fontSize: 13, marginBottom: 12 }}>{pwErr}</div>}
            {pwMsg && <div style={{ padding: '8px 12px', background: '#f0fdf4', border: '1px solid #86efac', borderRadius: 6, color: '#16a34a', fontSize: 13, marginBottom: 12 }}>{pwMsg}</div>}
            <div style={{ marginBottom: 14 }}>
              <label style={{ display: 'block', fontSize: 13, fontWeight: 500, color: '#555', marginBottom: 5 }}>Aktuelles Passwort</label>
              <input type="password" value={curPw} onChange={e => setCurPw(e.target.value)} required style={inp} />
            </div>
            <div style={{ marginBottom: 14 }}>
              <label style={{ display: 'block', fontSize: 13, fontWeight: 500, color: '#555', marginBottom: 5 }}>Neues Passwort</label>
              <input type="password" value={newPw} onChange={e => setNewPw(e.target.value)} required placeholder="Min. 8 Zeichen" style={inp} />
            </div>
            <div style={{ marginBottom: 14 }}>
              <label style={{ display: 'block', fontSize: 13, fontWeight: 500, color: '#555', marginBottom: 5 }}>Bestätigen</label>
              <input type="password" value={newPw2} onChange={e => setNewPw2(e.target.value)} required style={inp} />
            </div>
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 20 }}>
              <button type="button" onClick={onClose} style={cancelBtn}>Schließen</button>
              <button type="submit" disabled={pwSaving} style={saveBtn}>{pwSaving ? 'Speichern...' : 'Ändern'}</button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}

const inp: React.CSSProperties = { width: '100%', boxSizing: 'border-box', padding: '9px 11px', border: '1px solid #ddd', borderRadius: 7, fontSize: 14, outline: 'none' };
const saveBtn: React.CSSProperties = { padding: '9px 18px', background: '#C8402A', color: '#fff', border: 'none', borderRadius: 7, fontSize: 14, fontWeight: 600, cursor: 'pointer' };
const cancelBtn: React.CSSProperties = { padding: '9px 18px', background: '#fff', color: '#555', border: '1px solid #ddd', borderRadius: 7, fontSize: 14, cursor: 'pointer' };
